import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { connect } from "react-redux";
import { Dispatch } from "redux";

import { ProductSupplementsView } from './product-supplements.view';
import { ProductDataService } from '../product-form.data';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { actionCreators } from "src/store/products/product-form-nav-actions";

interface PropsFromState {
	childrenProducts?: ProductItem[];
}

interface PropsFromDispatch {
	navigateToChild: (
		child: ProductItem,
		title: string,
		text: string,
		key: string,
		product: ProductItem,
		defaultLanguage: string,
	) => any;
	childrenLoaded: (product: ProductItem, childrenProducts: ProductItem[]) => any;
}

interface Props extends ComponentViewProps<ProductItem> {
	mode: ControlMode;
	contextService: any;
	onModeChanged: (mode: ControlMode) => void;
	defaultLanguage: string;
}

@observer
class ProductSupplementsController extends React.Component<Props & PropsFromDispatch & PropsFromState, any> {
	@observable
	protected dataService: ProductDataService;

	@resolve
	protected toast: ToastNotifications;

	public componentDidMount() {
		this.loadData();
	}

	protected onItemDoubleClicked = async (item: ProductItem) => {
		const { model, defaultLanguage, navigateToChild, childrenLoaded } = this.props;
		const errors = await model.validate(false);
		if (errors.length != 0) {
			this.toast.showToast('Validation error!', errors.map(o => o.text), 'error');
			return;
		}

		if (item == null) {
			item = await this.dataService.add("SupplementDiscount", defaultLanguage, model);
			childrenLoaded(model, [item]);
		}
		const text = item.dynamicProperties['displayName'][defaultLanguage] || 'New';
		navigateToChild(item, 'Supplement/Discount', text, 'sup-disc', model, defaultLanguage);
	}

	protected async loadData(): Promise<void> {
		const { model, childrenProducts, setBusy, childrenLoaded, contextService } = this.props;

		this.dataService = ProductDataService.make(contextService);

		if ((childrenProducts == null || childrenProducts.length === 0) && model.id) {
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.addFilter('isDeleted', 'eq', false);
			this.dataService.addFilter('parentProductId', 'eq', model.id);
			this.dataService.addFilter('productType', 'eq', 6);
			this.dataService.pageSize = 25;
			try {
				setBusy(true);
				const items = await this.dataService.load(query => query);
				childrenLoaded(model, items.slice());
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				setBusy(false);
			}
		} else {
			this.dataService.items = childrenProducts.filter(e => e.productType === 6);
		}
	}

	public render() {
		if (this.dataService == null) {
			return null;
		}

		return (
			<ProductSupplementsView
				mode={this.props.mode}
				model={this.dataService}
				setBusy={this.props.setBusy}
				onItemDoubleClicked={this.onItemDoubleClicked}
			/>
		);
	}
}

const mapStateToProps = (
	state: IApplicationState,
	props: ComponentViewProps<ProductItem>
): PropsFromState => {
	const product = state.otherLoadedEntities.find(e => e.entity == props.model.entity) as ProductItem;
	return {
		childrenProducts: product.childrenProducts
			? product.childrenProducts.filter(e => e.productType == 6)
			: undefined,
	}
};

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	navigateToChild: (
		entity,
		title: string,
		text: string,
		key: string,
		product: ProductItem,
		defaultLanguage: string,
	) => dispatch(actionCreators.productFormUpNav(entity, title, text, key, {
		entity: product,
		key: "product",
		title: "Product",
		text: product.dynamicProperties['displayName'][defaultLanguage],
	})),
	childrenLoaded: (
		product: ProductItem,
		childrenProducts: ProductItem[]
	) => dispatch(actionCreators.productFormRelationshipLoaded(product, childrenProducts, "childrenProducts")),
});

const connected = connect(mapStateToProps, mapDispatchToProps)(ProductSupplementsController);
export { connected as ProductSupplementsController };



// WEBPACK FOOTER //
// ./src/app/products/products/form/parts/product-supplements.controller.tsx